import React, { useState } from 'react';
import { Services } from 'services';
import { BoxLoader } from 'components/loaders';
import { ShouldRender } from 'components/ShouldRender';
import { FormattedMessage, useIntl } from 'react-intl';
import { Flex } from '@clariness/flex';
import { TextField } from '@clariness/clarikit-react';
import { ServiceError } from 'components/ServiceError';
import { Button } from '@clariness/button';
import { Box } from '@clariness/box';
import { Text } from '@clariness/text';
import { Events } from '@clariness/tracking-event-runtime';
import { Store } from 'store';
import { captureEventV2 } from 'utils/events/captureEvent';
import { usePatientDetailsContext } from '../../ReferralDetails';

export const PatientDetailsNotes: React.FC = () => {
  const { currentReferral } = usePatientDetailsContext();
  const { referral, isLoading, isError, refetch } =
    Services.Referrals.useGetReferral(currentReferral?.id);
  const [note, setNote] = useState<string>('');
  const { user } = Store.useUser();
  const intl = useIntl();

  const { updateReferralNote, isLoading: isSavingReferralNote } =
    Services.Referrals.useUpdateReferralNote({
      onSuccess: () => setNote(''),
    });

  const handleSaveClick = () => {
    captureEventV2(
      Events.InvestigatorService.InvestigatorAddedNote({
        InvestigatorID: user?.id || '',
        SiteID: currentReferral?.site?.id || '',
        StudyID: currentReferral?.study?.id || '',
      })
    );
    updateReferralNote({ id: referral?.id, note });
  };

  if (isError) {
    return (
      <ServiceError onRetry={refetch}>
        <FormattedMessage
          id="is.patient_details_modal.notes.generic_error_message"
          defaultMessage="Something went wrong while we tried to display the patient's notes."
        />
      </ServiceError>
    );
  }

  return (
    <Box
      sx={{
        width: '95%',
      }}
    >
      <Flex
        sx={{
          flexDirection: 'column',
        }}
      >
        <TextField
          label=""
          value={note || ''}
          placeholder={intl.formatMessage({
            id: 'is.patient_details_modal.notes.placeholder',
            defaultMessage: 'You can add your notes here.',
          })}
          onChange={(value) => setNote(value)}
          autoComplete="off"
        />
        <Text as="p" variant="small" sx={{ color: 'gray', mt: '8px' }}>
          <FormattedMessage
            id="is.patient_details_modal.notes.visibility_notice"
            defaultMessage="Notes entered are not currently visible to the internal team."
          />
        </Text>
      </Flex>

      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '20px',
          py: '20px',
        }}
      >
        <Button
          size="small"
          variant="secondary"
          disabled={note === referral?.note}
          loading={isSavingReferralNote}
          onClick={() => handleSaveClick()}
        >
          <FormattedMessage
            id="is.patient_details_modal.notes.save_button"
            defaultMessage="Save"
          />
        </Button>
      </Flex>

      <ShouldRender when={isLoading}>
        <BoxLoader />
      </ShouldRender>
    </Box>
  );
};
